/**
 * flatToElkHierarchical.js — Canvas V2 hierarchical ELK graph builder.
 *
 * Task #160. The V1 layout pipeline (`utils/ir/layoutIR.js`) sized
 * containers bottom-up without negotiating horizontal budget with siblings
 * or a trunk anchor, producing overlapping chrome on real workflows.
 * Rather than patching that, V2 feeds ELK a true hierarchical
 * `children[]` tree and lets `elk.layered` own all geometry — node
 * placement, container sizing, trunk alignment, orthogonal edge routing,
 * and per-branch padding — in a single pass.
 *
 * SCOPE (this cut — Switch only)
 * ──────────────────────────────
 * Switch constructs emit a container node with:
 *   - a head child (the diamond / rounded rect)
 *   - one child per case's body (laid linearly inside the parent container)
 *   - a synthetic "meet" child at the tail so all cases converge cleanly
 * Fork / DynamicFork / DoWhile / SubWorkflow containers in this cut degrade
 * to scalar children (they'll get their own hierarchical shape in #161–#162).
 *
 * INPUT
 * ─────
 * Accepts the same `{nodes, connections}` payload the Code-tab emits:
 *   - connections may be either {source, target, sourceHandle}
 *     (post-IR) or {from, to, fromPort} (legacy) — we normalize on the way in.
 *
 * OUTPUT
 * ──────
 * Returns an ELK graph object ready for `elk.layout(graph)`:
 *   {
 *     id: 'root',
 *     layoutOptions: {...},
 *     children: [{ id, width, height, kind, label, ... }, ...],
 *     edges:    [{ id, sources, targets, labels?, v2Meta }, ...],
 *   }
 *
 * Every child carries an `_v2` tag with:
 *   - kind          : 'leaf' | 'switchHead' | 'switchContainer' | 'caseContainer'
 *                   | 'meet' | 'start' | 'end' | 'terminate' | 'fork' | 'loop' | 'subWorkflow'
 *   - canvasType    : the raw canvas type from the input (for the node renderer)
 *   - label         : display label
 *   - sourceNode    : the flat-canvas node this was derived from (or null for synthetics)
 *
 * Every edge carries a `_v2` tag with:
 *   - branchLabel   : case/branch name, when applicable
 *   - variant       : 'trunk' | 'branch' | 'convergence'
 *
 * See `elkToReactFlow.js` for the sibling module that converts the laid-out
 * graph into React Flow node + edge props.
 */

import { flatToIR } from '../ir/flatToIR';
import { IR_KIND } from '../ir/kinds';

// ───────────────────────────────────────────────────────────────────
//  Geometry constants
// ───────────────────────────────────────────────────────────────────
// These match the IR/Conductor OSS baseline so side-by-side visual diffs make sense.
// They're intentionally centralized here so the renderer and the builder
// agree on size invariants.
export const V2_DIMS = Object.freeze({
  LEAF_W:           260,
  LEAF_H:           72,
  SWITCH_HEAD_W:    260,
  SWITCH_HEAD_H:    96,   // diamond-ish rounded rect
  // FORK — the head is slimmer than a switch head; JOIN matches the head so
  // the fan-in and fan-out have matching visual weight at the top and bottom
  // of the container. Conductor OSS renders both at 350×120 (0.74× gives 260×80).
  FORK_HEAD_W:      260,
  FORK_HEAD_H:      80,
  JOIN_W:           260,
  JOIN_H:           80,
  // DYNAMIC_FORK gets a slightly taller head so there's room to surface the
  // list expression without truncation — the whole point of V2's dyn-fork
  // treatment is to make the expression visible at def time.
  DYN_FORK_HEAD_W:  280,
  DYN_FORK_HEAD_H:  104,
  LOOP_HEAD_W:      260,
  LOOP_HEAD_H:      88,
  SUBWF_HEAD_W:     260,
  SUBWF_HEAD_H:     88,
  TERMINATE_W:      260,
  TERMINATE_H:      60,
  SENTINEL_W:       72,
  SENTINEL_H:       72,
  MEET_W:           28,   // small convergence dot
  MEET_H:           28,
  CONTAINER_PAD:    15,   // internal padding around container chrome
  CASE_PAD:         10,   // padding inside each case sub-container
  LEG_PAD:          10,   // padding inside each fork-leg sub-container
});

// ───────────────────────────────────────────────────────────────────
//  Public entry point
// ───────────────────────────────────────────────────────────────────

/**
 * Convert a flat canvas payload to an ELK hierarchical graph.
 *
 * Throws on malformed input (propagates `flatToIR`'s errors). Callers in the
 * UI should wrap in try/catch and fall back to a friendly error banner.
 *
 * @param {{ nodes: object[], connections: object[] }} canvas
 * @returns {object} ELK graph ready for `elk.layout()`.
 */
export function flatToElkHierarchical(canvas) {
  if (!canvas || !Array.isArray(canvas.nodes) || !Array.isArray(canvas.connections)) {
    throw new Error('flatToElkHierarchical: expected { nodes: [], connections: [] }');
  }

  // Normalize connection shape so flatToIR gets what it expects.
  const normalized = {
    nodes: canvas.nodes,
    connections: canvas.connections.map(normalizeConnection),
  };

  const ir = flatToIR(normalized);
  // Index the flat nodes by id — we keep a pointer back to the source node
  // on every emitted child so the renderer has access to the original
  // config (ports, expression, HTTP method, etc.) without a round-trip.
  const nodeIndex = new Map();
  for (const n of canvas.nodes) nodeIndex.set(n.id, n);

  const builder = new GraphBuilder(nodeIndex);
  builder.emitStart(ir.start);

  for (const irNode of ir.sequence) {
    builder.emitIRNode(irNode, /* parent */ builder.root);
  }

  // END sentinel consistency (per #165): every DAG must bookend with a round
  // END label — it's the Agentlets brand moment where the customer sees
  // value delivered. If the payload doesn't carry an explicit END node we
  // synthesize one so the bookend invariant holds on every creation path
  // (fresh canvas, Conductor OSS import, user-pasted JSON, fixture, etc.).
  if (ir.end) {
    builder.emitEnd(ir.end);
  } else {
    builder.emitSyntheticEnd();
  }

  // Thread the trunk: every top-level child in order forms the spine.
  // Connect them with plain 'trunk' edges, unless an internal container
  // already dangles the chain through its meet node (which we handle by
  // attaching the "next" edge's source to the container's output port).
  builder.stitchTrunk();

  // G267 — Cross-cutting edges (cognitive availability + return).
  //
  // The IR walker only emits edges that fit the structural taxonomy
  // (trunk / branch / convergence / loopBack). Cognitive availability
  // edges (loop → skill, "may invoke") and return edges (skill → loop)
  // don't fit any of those — they're cross-cutting meta-edges defined
  // by AGENT_EXECUTION_MODEL.md §4 that decorate the deterministic
  // spine with a tool catalog the LLM may invoke at runtime.
  //
  // These edges may reference skill nodes that are NOT in ELK's
  // structural layout (the IR walker doesn't reach them because they
  // dangle off cognitive_loop's auxiliary handles). ELK would either
  // warn-and-drop or error if we added the edges to its graph. Instead
  // we attach them to a sidecar `_crossCutting` array on the root;
  // `elkToReactFlow` projects them straight to React Flow edges,
  // bypassing ELK's polyline routing. V2CanvasView pins the dangling
  // skill orphans by canvas id (W133), so source/target ids resolve
  // cleanly when ReactFlow draws the edge as a straight line between
  // the loop's exit handle and the orphan's input handle.
  const crossCutting = [];
  /* G267c — ONE EDGE PER LOOP, pointing at the tool-catalog GROUP.
   *
   * The DATA MODEL carries two connections per tool — `cognitive_avail` (loop → tool, "the
   * LLM may invoke this") and `cognitive_return` (tool → loop, "the result feeds the next
   * iteration"). Both must stay: reachability (v2Lint/flatToIR treat avail as the forward
   * edge that makes the tool reachable) and the cycle machinery (return is a declared
   * back-edge) depend on them. We do NOT touch the data here — this is purely what we PAINT.
   *
   * Painting one route per tool never worked, in any variant we tried:
   *   - two polylines per tool  → starburst over the loop + "return" pills on the cards
   *   - one bidirectional route per tool, per-lane corridors → still N routes fanning across
   *     the trunk, and a horizontal run that happened to sit at the JOIN's y-coordinate read
   *     as an edge leaving the JOIN.
   *
   * The root problem is that we were drawing a SET as if it were a FLOW. The tools are not
   * sequenced and not forked — they're a catalog the LLM chooses from. So draw the set as a
   * set: V2CanvasView wraps the tool cards in one dotted group, and we emit exactly ONE
   * dashed edge from the loop to that group. N routes → 1. Nothing to overlap. */
  const loopsWithTools = new Map();   // loopId → Set(toolId)
  for (const c of normalized.connections) {
    if (!c || !c.source || !c.target) continue;
    const handle = c.sourceHandle;
    const isAvail = handle === 'cognitive_avail';
    const isReturn = handle === 'cognitive_return';
    if (!isAvail && !isReturn) continue;
    // On an availability edge the LOOP is the source; on a return edge it's the target.
    const loopId = isAvail ? c.source : c.target;
    const toolId = isAvail ? c.target : c.source;
    if (!loopsWithTools.has(loopId)) loopsWithTools.set(loopId, new Set());
    loopsWithTools.get(loopId).add(toolId);
  }
  for (const [loopId, toolIds] of loopsWithTools) {
    crossCutting.push({
      id: `xc_catalog__${loopId}`,
      source: loopId,
      target: cognitiveCatalogId(loopId),   // synthesised by V2CanvasView; not a canvas node
      variant: 'availability',
      label: null,                          // the group's own chip carries the caption
      /* Anchor sideways, not off the loop's bottom handle — see V2Leaf's `cog-out`. */
      sourceHandle: 'cog-out',
      targetHandle: 'cog-in',
      toolCount: toolIds.size,
    });
  }
  if (crossCutting.length > 0) {
    builder.root._crossCutting = crossCutting;
  }

  return builder.finalize();
}

/**
 * G267c — Id of the synthetic tool-catalog group for a cognitive loop.
 *
 * Shared by the cross-cutting edge emitted here (which targets it) and V2CanvasView (which
 * creates the group node). Exported rather than duplicated so the two can never drift — a
 * mismatch would silently drop the edge, which is exactly the class of bug that made the
 * "may invoke" edges vanish the first time round.
 */
export function cognitiveCatalogId(loopId) {
  return `__cog_catalog__${loopId}`;
}

// ───────────────────────────────────────────────────────────────────
//  Connection-shape normalization
// ───────────────────────────────────────────────────────────────────

function normalizeConnection(c) {
  const source = c && c.source != null ? c.source : (c && c.from);
  const target = c && c.target != null ? c.target : (c && c.to);
  const sourceHandle = c && c.sourceHandle != null
    ? c.sourceHandle
    : (c && c.fromPort != null ? c.fromPort : 'default');
  const label = c && c.label != null ? c.label : undefined;
  // G267 — preserve `style` (e.g. 'dashed') so cross-cutting edge emission
  // below can map it to the correct ELK edge variant for rendering.
  const style = c && c.style != null ? c.style : undefined;
  return {
    source, target, sourceHandle,
    ...(label !== undefined ? { label } : {}),
    ...(style !== undefined ? { style } : {}),
  };
}

// ───────────────────────────────────────────────────────────────────
//  Builder — stateful accumulator over one conversion
// ───────────────────────────────────────────────────────────────────

// Defensive guards against pathological IR. The recursive descent in
// `emitIRNode` → `_emitSwitch` / `_emitForkLike` / `_emitLoop` /
// `_emitSubWorkflow` had no cycle protection, so any IR with a cycle
// (e.g. a SUB_WORKFLOW whose `_embeddedCanvas` contains a node id
// matching itself, or the canvas it lives in) would recurse until V8
// blew the stack — the user-facing symptom was the red banner
// "Layout error: Maximum call stack size exceeded" with an otherwise
// blank canvas.
//
// Two layers, belt and braces:
//   1. Visited-set on IR node ids — catches the most likely cause
//      (SUB_WORKFLOW self-reference / circular embed). Same id seen
//      twice ⇒ render the offending node as an opaque leaf and stop
//      descending, preserving the rest of the layout.
//   2. Hard depth ceiling — defends against legitimate-looking but
//      pathological deep chains (A→B→C→…→Z→A` where every id differs
//      but the chain is degenerate). 64 is roughly 5x deeper than any
//      hand-authored workflow we've seen.
const MAX_EMIT_DEPTH = 64;

class GraphBuilder {
  constructor(nodeIndex) {
    this._nodeIndex = nodeIndex;
    this._edgeCounter = 0;
    this._childCounter = 0;
    // ── Cycle / depth guards (see comment block above). _emitDepth is
    // bumped on entry to emitIRNode and decremented on exit.
    // _visitedIds tracks IR node ids currently on the descent stack so
    // a self-reference is caught as soon as we see the same id twice.
    this._emitDepth = 0;
    this._visitedIds = new Set();

    this.root = {
      id: 'root',
      layoutOptions: rootLayoutOptions(),
      children: [],
      edges: [],
      _v2: { kind: 'root' },
    };
    // Every child we push is also recorded in this linear list so stitchTrunk
    // can walk it in insertion order.
    this._rootOrder = [];
  }

  finalize() {
    return this.root;
  }

  // ── Sentinels ─────────────────────────────────────────────────────

  emitStart(startIR) {
    const src = this._nodeIndex.get(startIR.id) || null;
    const node = this._newChild({
      id: this._qualify('start', startIR.id),
      width: V2_DIMS.SENTINEL_W,
      height: V2_DIMS.SENTINEL_H,
      _v2: {
        kind: 'start',
        label: (src && src.label) || 'Start',
        canvasType: (src && src.type) || 'start',
        sourceNodeId: startIR.id,
      },
    });
    this.root.children.push(node);
    this._rootOrder.push(node);
  }

  emitEnd(endIR) {
    const src = this._nodeIndex.get(endIR.id) || null;
    const node = this._newChild({
      id: this._qualify('end', endIR.id),
      width: V2_DIMS.SENTINEL_W,
      height: V2_DIMS.SENTINEL_H,
      _v2: {
        kind: 'end',
        label: (src && src.label) || 'End',
        canvasType: (src && src.type) || 'end',
        sourceNodeId: endIR.id,
      },
    });
    this.root.children.push(node);
    this._rootOrder.push(node);
  }

  /**
   * Synthesize an END sentinel when the IR didn't hand us one. This keeps
   * the round END label consistent across every DAG creation path — fresh
   * fixtures, Conductor OSS imports, user-pasted JSON, and incomplete drafts.
   *
   * The synthetic node has no `sourceNodeId` (there's nothing in the flat
   * payload to tie it back to); downstream code that cares about the
   * distinction can check `_v2.synthetic === true`.
   */
  emitSyntheticEnd() {
    const node = this._newChild({
      id: this._qualify('end', '__synthetic__'),
      width: V2_DIMS.SENTINEL_W,
      height: V2_DIMS.SENTINEL_H,
      _v2: {
        kind: 'end',
        label: 'End',
        canvasType: 'end',
        sourceNodeId: null,
        synthetic: true,
      },
    });
    this.root.children.push(node);
    this._rootOrder.push(node);
  }

  // ── Dispatcher ───────────────────────────────────────────────────

  emitIRNode(ir, parent) {
    // ── Cycle / depth guards (see GraphBuilder constructor comment).
    // Two failure modes we want to survive without nuking the canvas:
    //   - Same IR id seen twice on the descent stack → self-reference
    //   - Descent deeper than MAX_EMIT_DEPTH → degenerate chain
    // In both cases we synthesise a stub leaf so the rest of the
    // layout still renders (the offending subtree is collapsed but
    // the user can still see and edit everything around it).
    const irId = ir && ir.id;
    if (irId && this._visitedIds.has(irId)) {
      return this._emitGuardStub(ir, parent, 'circular');
    }
    if (this._emitDepth >= MAX_EMIT_DEPTH) {
      return this._emitGuardStub(ir, parent, 'depth');
    }

    if (irId) this._visitedIds.add(irId);
    this._emitDepth += 1;
    try {
      switch (ir.kind) {
        case IR_KIND.LEAF:        return this._emitLeaf(ir, parent);
        case IR_KIND.TERMINATE:   return this._emitTerminate(ir, parent);
        case IR_KIND.SWITCH:      return this._emitSwitch(ir, parent);
        case IR_KIND.FORK:        return this._emitForkLike(ir, parent, 'fork');
        case IR_KIND.DYNAMIC_FORK:return this._emitForkLike(ir, parent, 'dynamicFork');
        case IR_KIND.DO_WHILE:    return this._emitLoop(ir, parent);
        case IR_KIND.SUB_WORKFLOW:return this._emitSubWorkflow(ir, parent);
        default:
          throw new Error(`flatToElkHierarchical: unknown IR kind '${ir.kind}'`);
      }
    } finally {
      this._emitDepth -= 1;
      if (irId) this._visitedIds.delete(irId);
    }
  }

  /**
   * Render a placeholder leaf when we detect a cycle or runaway depth
   * during recursive descent. Keeps the rest of the layout intact so
   * the user gets a partially-rendered canvas with a clearly-labelled
   * "circular reference" stub instead of a blanket layout failure.
   */
  _emitGuardStub(ir, parent, reason) {
    const src = (ir && ir.id && this._nodeIndex.get(ir.id)) || null;
    const baseLabel = (src && src.label) || (ir && ir.id) || 'Unknown';
    const label = reason === 'circular'
      ? `${baseLabel} (⚠ circular reference)`
      : `${baseLabel} (⚠ nesting too deep)`;
    // eslint-disable-next-line no-console
    console.warn('[flatToElkHierarchical] guard tripped:', reason, 'on', ir && ir.id);
    const node = this._newChild({
      id: this._qualify('guard', (ir && ir.id) || 'unknown'),
      width: V2_DIMS.LEAF_W,
      height: V2_DIMS.LEAF_H,
      _v2: {
        kind: 'leaf',
        label,
        canvasType: (src && src.type) || 'task',
        sourceNodeId: (ir && ir.id) || null,
        guardStub: true,
        guardReason: reason,
      },
    });
    parent.children.push(node);
    return { entry: node, exit: node, terminates: false };
  }

  // ── Leaves / Terminates ──────────────────────────────────────────

  _emitLeaf(ir, parent) {
    const src = this._nodeIndex.get(ir.id) || null;
    const node = this._newChild({
      id: this._qualify('leaf', ir.id),
      width: V2_DIMS.LEAF_W,
      height: V2_DIMS.LEAF_H,
      _v2: {
        kind: 'leaf',
        label: ir.label,
        canvasType: ir.canvasType,
        sourceNodeId: ir.id,
        props: ir.props || {},
      },
    });
    parent.children.push(node);
    if (parent === this.root) this._rootOrder.push(node);
    return { entry: node, exit: node };
  }

  _emitTerminate(ir, parent) {
    const node = this._newChild({
      id: this._qualify('terminate', ir.id),
      width: V2_DIMS.TERMINATE_W,
      height: V2_DIMS.TERMINATE_H,
      _v2: {
        kind: 'terminate',
        label: ir.label,
        reason: ir.reason,
        status: ir.status,
        sourceNodeId: ir.id,
      },
    });
    parent.children.push(node);
    if (parent === this.root) this._rootOrder.push(node);
    return { entry: node, exit: node, terminates: true };
  }

  _emitSubWorkflow(ir, parent) {
    // Two shapes:
    //   (a) scalar: unresolved ref or async fire-and-forget — renders as a
    //       single head-style tile carrying {ref, version, async}.
    //   (b) container: embedded body is non-null — renders as a nested DAG
    //       chrome with the embedded IR walked recursively (Canvas V2's
    //       drill-in "see what this sub-workflow does without leaving the
    //       canvas" affordance — Conductor OSS makes you navigate away).
    //
    // Embedded body sources, in priority order:
    //   1. `ir.embedded` from flatToIR (production path once IR carries it).
    //   2. `config._embeddedCanvas` on the source canvas node — a full
    //      `{nodes, connections}` payload representing the child workflow.
    //      This is Canvas V2's flagship drill-in path (#168): user clicks a
    //      SubWorkflow, we fetch the child's canvas JSON and stamp it here
    //      so the child's full IR (with its own switches / forks / loops)
    //      nests inside the parent container.
    //   3. `config._embeddedDagPreview` on the source canvas node — a
    //      lightweight array of `{id, type, label, config?}` objects the v2
    //      builder synthesises into Leaf IR nodes. Used for shallow previews
    //      where we only want to show the task list, not the full DAG.
    let embeddedSequence = null;
    const irEmb = ir.embedded;
    if (Array.isArray(irEmb) && irEmb.length > 0) {
      embeddedSequence = irEmb;
    } else if (irEmb && typeof irEmb === 'object' && irEmb.kind) {
      embeddedSequence = [irEmb];
    } else {
      const src = this._nodeIndex.get(ir.id) || null;
      const cfg = (src && src.config) || {};

      // Tier 2: full child canvas — run it through flatToIR and take the
      // resulting sequence. We also merge the child's flat nodes into the
      // parent builder's nodeIndex so downstream lookups (source config,
      // labels, etc.) resolve against the child's definitions.
      const childCanvas = cfg._embeddedCanvas;
      if (childCanvas && Array.isArray(childCanvas.nodes) && Array.isArray(childCanvas.connections)) {
        try {
          const childIR = flatToIR({
            nodes: childCanvas.nodes,
            connections: childCanvas.connections.map(normalizeConnection),
          });
          for (const n of childCanvas.nodes) {
            if (n && n.id && !this._nodeIndex.has(n.id)) {
              this._nodeIndex.set(n.id, n);
            }
          }
          if (Array.isArray(childIR.sequence) && childIR.sequence.length > 0) {
            embeddedSequence = childIR.sequence;
          }
        } catch (err) {
          // If the child payload is malformed, fall through to the preview
          // hint or scalar — drill-in should never break the parent layout.
          // eslint-disable-next-line no-console
          console.warn('[flatToElkHierarchical] embedded canvas failed to build IR', err);
        }
      }

      // Tier 3: shallow preview (flat list of steps).
      if (!embeddedSequence) {
        const preview = cfg._embeddedDagPreview;
        if (Array.isArray(preview) && preview.length > 0) {
          embeddedSequence = preview.map((p) => ({
            kind: IR_KIND.LEAF,
            id: p.id || `${ir.id}__preview__${p.label || 'step'}`,
            label: p.label || p.id || 'Step',
            canvasType: p.type || 'task',
            props: p.config || {},
          }));
        }
      }
    }
    if (embeddedSequence) return this._emitSubWorkflowContainer(ir, parent, embeddedSequence);
    return this._emitSubWorkflowScalar(ir, parent);
  }

  _emitSubWorkflowScalar(ir, parent) {
    const node = this._newChild({
      id: this._qualify('subwf', ir.id),
      width: V2_DIMS.SUBWF_HEAD_W,
      height: V2_DIMS.SUBWF_HEAD_H,
      _v2: {
        kind: 'subWorkflow',
        label: ir.label,
        canvasType: 'sub_workflow',
        refName: ir.refName,
        refVersion: ir.refVersion,
        async: !!ir.async,
        sourceNodeId: ir.id,
      },
    });
    parent.children.push(node);
    if (parent === this.root) this._rootOrder.push(node);
    return { entry: node, exit: node };
  }

  _emitSubWorkflowContainer(ir, parent, embeddedSequence) {
    // Chrome: container + head tile + inner DAG of the embedded body.
    const container = this._newChild({
      id: this._qualify('subwf_c', ir.id),
      layoutOptions: subWorkflowContainerLayoutOptions(),
      children: [],
      edges: [],
      _v2: {
        kind: 'subWorkflowContainer',
        label: ir.label,
        canvasType: 'sub_workflow',
        refName: ir.refName,
        refVersion: ir.refVersion,
        async: !!ir.async,
        sourceNodeId: ir.id,
      },
    });

    const head = this._newChild({
      id: this._qualify('subwf_head', ir.id),
      width: V2_DIMS.SUBWF_HEAD_W,
      height: V2_DIMS.SUBWF_HEAD_H,
      _v2: {
        kind: 'subWorkflowHead',
        label: ir.label,
        canvasType: 'sub_workflow',
        refName: ir.refName,
        refVersion: ir.refVersion,
        async: !!ir.async,
        sourceNodeId: ir.id,
      },
    });
    container.children.push(head);

    // Walk the embedded IR sequence as children of the container, chaining
    // with trunk edges. This mirrors how Switch cases walk their children.
    let prevExit = null;
    let bodyEntry = null;
    let bodyExit = null;
    for (const child of embeddedSequence) {
      const { entry, exit, terminates } = this.emitIRNode(child, container);
      if (prevExit) {
        const sourceId = _pickTrunkTail(prevExit) || prevExit.id;
        this._addEdge(container, sourceId, entry.id, { variant: 'trunk' });
      } else {
        bodyEntry = entry;
        // Head → first embedded child.
        this._addEdge(container, head.id, entry.id, { variant: 'trunk' });
      }
      prevExit = terminates ? null : exit;
      bodyExit = prevExit || bodyExit;
    }
    // If the embedded sequence existed but ended on TERMINATE, bodyExit may
    // be null — fall back to head so the outer stitcher still has an anchor.
    if (!bodyExit) bodyExit = head;

    parent.children.push(container);
    if (parent === this.root) this._rootOrder.push(container);
    return { entry: container, exit: container, tailPort: bodyExit };
  }

  // ── Switch — this cut's flagship ─────────────────────────────────

  _emitSwitch(ir, parent) {
    const src = this._nodeIndex.get(ir.id) || null;

    // The container node — this is the group ELK will size around head +
    // cases. The head and each case live as children INSIDE this container.
    const container = this._newChild({
      id: this._qualify('switch', ir.id),
      layoutOptions: switchContainerLayoutOptions(),
      children: [],
      edges: [],
      _v2: {
        kind: 'switchContainer',
        label: ir.label,
        // A Switch is the IR for "one arm runs", which a policy gate, a DPG
        // scan, an approval and an RBAC check all are as much as a condition
        // node is. Those carry their originating type so the container keeps
        // the node's own chrome; a condition node carries none and falls back
        // to what this always emitted.
        canvasType: ir.canvasType || 'condition',
        sourceNodeId: ir.id,
      },
    });

    // Switch head — the diamond / rounded rect.
    const head = this._newChild({
      id: this._qualify('switch_head', ir.id),
      width: V2_DIMS.SWITCH_HEAD_W,
      height: V2_DIMS.SWITCH_HEAD_H,
      _v2: {
        kind: 'switchHead',
        label: ir.label,
        canvasType: ir.canvasType || 'condition',
        expression: ir.expr,
        sourceNodeId: ir.id,
      },
    });
    container.children.push(head);

    // Build every case — including the default if present. Each case becomes
    // its own sub-container to hold any scalar children and give the renderer
    // a place to paint per-case chrome.
    const allCases = ir.defaultCase ? [...ir.cases, ir.defaultCase] : ir.cases;
    const caseExits = [];

    for (const c of allCases) {
      const caseContainer = this._newChild({
        id: this._qualify('case', `${ir.id}__${c.name}`),
        layoutOptions: caseContainerLayoutOptions(),
        children: [],
        edges: [],
        _v2: {
          kind: 'caseContainer',
          label: c.name,
          branchName: c.name,
          isDefault: !!c.isDefault,
          ownerSwitchId: ir.id,
        },
      });
      container.children.push(caseContainer);

      // Head edge: switch head → case container. ELK draws the edge into
      // the container's boundary; the `INCLUDE_CHILDREN` hierarchy handling
      // then threads it to the first child inside.
      let caseEntry = caseContainer;
      let caseExit = caseContainer;

      if (c.children.length > 0) {
        // Emit children and chain them.
        let prevExit = null;
        for (const child of c.children) {
          const { entry, exit, terminates } = this.emitIRNode(child, caseContainer);
          if (prevExit) {
            this._addEdge(caseContainer, prevExit.id, entry.id, { variant: 'trunk' });
          } else {
            caseEntry = entry;
          }
          prevExit = terminates ? null : exit;
        }
        caseExit = prevExit || caseEntry;
      } else {
        // Empty case — synthesize a tiny pass-through node so the case
        // container still has a child ELK can size around and edges can
        // attach to.
        const passthrough = this._newChild({
          id: this._qualify('case_empty', `${ir.id}__${c.name}`),
          width: V2_DIMS.MEET_W,
          height: V2_DIMS.MEET_H,
          _v2: { kind: 'meet', label: '', ownerSwitchId: ir.id, branchName: c.name },
        });
        caseContainer.children.push(passthrough);
        caseEntry = passthrough;
        caseExit = passthrough;
      }

      // G479 — Switch head → case ENTRY (the case's first inner node), not
      // case CONTAINER. With ELK's `INCLUDE_CHILDREN` hierarchyHandling, the
      // edge path still routes through the container boundary visually
      // (because the target lives inside that container), but the React Flow
      // rfEdge now resolves to the actual inner leaf/switch/terminate id.
      //
      // Pre-G479, the edge target was `caseContainer.id` — ELK threaded the
      // visual path correctly but the rfEdge's `target` remained the
      // container id. The inner content (leaves, terminates) ended up with
      // ZERO inbound rfEdges, which broke any downstream code that walked
      // the rfEdge graph to compute reachability, and made arrows visually
      // stop at the case-container border rather than reaching the content
      // inside. Cf. insurance_claim v44's 5 stranded terminates + the
      // "leg containers look empty" pattern on the Untitled / IdentityOnboadring
      // workflows.
      this._addEdge(container, head.id, caseEntry.id, {
        variant: 'branch',
        branchLabel: c.name,
        isDefault: !!c.isDefault,
      });

      caseExits.push({ caseContainer, caseEntry, caseExit, terminates: _isTerminatedCase(c) });
    }

    // G481 — Convergence: synthesize a meet node ONLY if at least one case
    // can reach it (i.e., at least one non-terminating arm exists). When
    // every arm of the switch terminates (the all-default→terminate pattern
    // seen on insurance_claim v44's `after_investigation`), no convergence
    // is possible — emitting a meet just leaves a stranded chrome dot
    // floating in the layout with no inbound edges. Skip it instead and
    // let stitchTrunk fall back to the container id via _pickTrunkTail.
    const hasConverging = caseExits.some((e) => !e.terminates);
    let meet = null;
    if (hasConverging) {
      meet = this._newChild({
        id: this._qualify('switch_meet', ir.id),
        width: V2_DIMS.MEET_W,
        height: V2_DIMS.MEET_H,
        _v2: {
          kind: 'meet',
          label: '',
          ownerSwitchId: ir.id,
        },
      });
      container.children.push(meet);

      // G479 — Mirror of the entry-side fix: emit convergence from each
      // case's EXIT child (the last actual inner node), not from the case
      // container itself. ELK still routes the path through the container
      // boundary.
      for (const exit of caseExits) {
        if (exit.terminates) continue;
        this._addEdge(container, exit.caseExit.id, meet.id, {
          variant: 'convergence',
        });
      }
    }

    parent.children.push(container);
    if (parent === this.root) this._rootOrder.push(container);
    return {
      entry: container,
      exit: container,
      // Exposed for the trunk stitcher — when a meet exists, it's the
      // tail-port. When no arm converges (all terminate), this is null and
      // _pickTrunkTail falls back to the container id; the next trunk edge
      // attaches to the container boundary, which is fine because the
      // workflow can't actually continue past an all-terminating switch.
      tailPort: meet,
    };
  }

  // ── Fork / DynamicFork — hierarchical container + legs + JOIN (task #161)
  //
  // Topology (same skeleton for both variants; distinguished by container
  // kind and the synthetic leg naming for dynamic fork):
  //
  //   forkContainer (cyan) | dynForkContainer (violet)
  //     ├── forkHead       | dynForkHead
  //     ├── legContainer[0..N-1]   (dyn-fork: single template leg)
  //     │     └── …leg IR children
  //     └── joinNode       (replaces the switch's meet dot)
  //
  // Intentional differences from Conductor OSS's rendering:
  //   - Every leg has its own container chrome with a "leg N" / handle-name
  //     tag. Conductor OSS leaves fork legs anonymous and ungrouped.
  //   - JOIN surfaces wait-mode (ALL / ANY / N-of-M) on-node.
  //   - Dynamic fork exposes the list expression and a single template leg,
  //     so customers see the shape of what'll run in parallel even before
  //     runtime resolves `dynamicTasks`.

  _emitForkLike(ir, parent, variantKind) {
    if (variantKind === 'dynamicFork') return this._emitDynamicFork(ir, parent);
    return this._emitFork(ir, parent);
  }

  _emitFork(ir, parent) {
    const containerKind = 'forkContainer';
    const headKind      = 'forkHead';

    const container = this._newChild({
      id: this._qualify('fork', ir.id),
      layoutOptions: forkContainerLayoutOptions(),
      children: [],
      edges: [],
      _v2: {
        kind: containerKind,
        label: ir.label,
        canvasType: 'parallel_fork',
        sourceNodeId: ir.id,
        legCount: (ir.branches || []).length,
      },
    });

    const head = this._newChild({
      id: this._qualify('fork_head', ir.id),
      width: V2_DIMS.FORK_HEAD_W,
      height: V2_DIMS.FORK_HEAD_H,
      _v2: {
        kind: headKind,
        label: ir.label,
        canvasType: 'parallel_fork',
        sourceNodeId: ir.id,
        legCount: (ir.branches || []).length,
      },
    });
    container.children.push(head);

    const legExits = [];
    const branches = Array.isArray(ir.branches) ? ir.branches : [];
    branches.forEach((b, idx) => {
      const legName = (b && b.name) || `branch_${idx}`;
      const legContainer = this._newChild({
        id: this._qualify('leg', `${ir.id}__${legName}`),
        layoutOptions: legContainerLayoutOptions(),
        children: [],
        edges: [],
        _v2: {
          kind: 'legContainer',
          label: legName,
          branchName: legName,
          legIndex: idx,
          ownerForkId: ir.id,
        },
      });
      container.children.push(legContainer);

      // Walk the leg's IR children, chaining them with trunk edges inside
      // the leg container. Mirrors the Switch case handling.
      let legEntry = legContainer;
      let legExit  = legContainer;
      if (Array.isArray(b.children) && b.children.length > 0) {
        let prevExit = null;
        for (const child of b.children) {
          const { entry, exit, terminates } = this.emitIRNode(child, legContainer);
          if (prevExit) {
            this._addEdge(legContainer, prevExit.id, entry.id, { variant: 'trunk' });
          } else {
            legEntry = entry;
          }
          prevExit = terminates ? null : exit;
        }
        legExit = prevExit || legEntry;
      } else {
        // Empty leg — synthesize a meet pass-through so ELK has an anchor.
        const passthrough = this._newChild({
          id: this._qualify('leg_empty', `${ir.id}__${legName}`),
          width: V2_DIMS.MEET_W,
          height: V2_DIMS.MEET_H,
          _v2: { kind: 'meet', label: '', ownerForkId: ir.id, branchName: legName },
        });
        legContainer.children.push(passthrough);
        legEntry = passthrough;
        legExit  = passthrough;
      }

      // G479 — Head → leg ENTRY (the leg's first inner node), not leg
      // CONTAINER. Mirrors the case-entry fix in _emitSwitch — gives the
      // first child a real inbound rfEdge so React Flow's edge model
      // reflects the structural reachability. ELK still routes the path
      // through the leg container boundary via INCLUDE_CHILDREN.
      this._addEdge(container, head.id, legEntry.id, {
        variant: 'branch',
        branchLabel: null,        // explicit null suppresses label pill
      });

      legExits.push({ legContainer, legEntry, legExit, terminates: _isTerminatedBranch(b) });
    });

    // JOIN — replaces the switch's small meet dot with a full-size JOIN node
    // carrying wait-mode + joinOn metadata. Label comes from the paired
    // parallel_join IR, with a fallback to the fork's own name.
    const joinIR = ir.join || {};
    const join = this._newChild({
      id: this._qualify('fork_join', ir.id),
      width: V2_DIMS.JOIN_W,
      height: V2_DIMS.JOIN_H,
      _v2: {
        kind: 'join',
        label: joinIR.label || 'Join',
        canvasType: 'parallel_join',
        waitMode: joinIR.waitMode || 'ALL',       // 'ALL' | 'ANY' | 'N_OF_M'
        waitCount: joinIR.waitCount || null,       // for 'N_OF_M'
        joinOn: Array.isArray(joinIR.joinOn) ? joinIR.joinOn : [],
        legCount: branches.length,
        ownerForkId: ir.id,
        sourceNodeId: joinIR.id || null,
      },
    });
    container.children.push(join);

    // G479 — Leg EXIT → join (not legContainer → join). Same rationale as
    // the case-exit → meet fix in _emitSwitch.
    for (const exit of legExits) {
      if (exit.terminates) continue;
      this._addEdge(container, exit.legExit.id, join.id, {
        variant: 'convergence',
      });
    }

    parent.children.push(container);
    if (parent === this.root) this._rootOrder.push(container);
    return {
      entry: container,
      exit: container,
      // Trunk stitcher needs the join as the next trunk edge's source.
      tailPort: join,
    };
  }

  _emitDynamicFork(ir, parent) {
    // Dynamic fork has two def-time shapes:
    //   (a) unresolved: ir.resolvedBranches is null — we render a single
    //       "template" leg as a stand-in with the list expression visible.
    //   (b) resolved preview: ir.resolvedBranches is an array — render
    //       exactly like a static fork, but with the dynamic chrome colour.
    //
    // Either way, JOIN is present. The IR type itself doesn't carry the
    // listExpression / concurrencyLimit fields yet, so pull them off the
    // source canvas node's config — that's what gives V2 its def-time
    // transparency advantage over Conductor OSS.
    const srcNode = this._nodeIndex.get(ir.id) || null;
    const cfg = (srcNode && srcNode.config) || {};
    // Try a few aliases — Conductor OSS import uses `dynamicTaskNameParam`,
    // Conductor-native uses `inputParameters.dynamicTasksInput`, our own
    // design-time model uses `listExpression`. First match wins.
    const listExpression =
      cfg.listExpression
      || cfg.dynamicTasksInput
      || cfg.dynamicTaskNameParam
      || null;
    const concurrencyLimit =
      (cfg.concurrencyLimit != null ? cfg.concurrencyLimit : null)
      || (cfg.maxConcurrency != null ? cfg.maxConcurrency : null);

    const container = this._newChild({
      id: this._qualify('dynfork', ir.id),
      layoutOptions: forkContainerLayoutOptions(),
      children: [],
      edges: [],
      _v2: {
        kind: 'dynForkContainer',
        label: ir.label,
        canvasType: 'parallel_fork',
        sourceNodeId: ir.id,
        dynamic: true,
        listExpression,
        concurrencyLimit,
      },
    });

    const head = this._newChild({
      id: this._qualify('dynfork_head', ir.id),
      width: V2_DIMS.DYN_FORK_HEAD_W,
      height: V2_DIMS.DYN_FORK_HEAD_H,
      _v2: {
        kind: 'dynForkHead',
        label: ir.label,
        canvasType: 'parallel_fork',
        sourceNodeId: ir.id,
        listExpression,
        concurrencyLimit,
      },
    });
    container.children.push(head);

    const resolved = Array.isArray(ir.resolvedBranches) && ir.resolvedBranches.length > 0;
    const legsToEmit = resolved ? ir.resolvedBranches : [{ name: 'template', children: [] }];
    const legExits = [];

    legsToEmit.forEach((b, idx) => {
      const legName = (b && b.name) || `item_${idx}`;
      const legContainer = this._newChild({
        id: this._qualify('dynleg', `${ir.id}__${legName}`),
        layoutOptions: legContainerLayoutOptions(),
        children: [],
        edges: [],
        _v2: {
          kind: resolved ? 'legContainer' : 'dynLegTemplate',
          label: legName,
          branchName: legName,
          legIndex: idx,
          ownerForkId: ir.id,
          isTemplate: !resolved,   // drives the "for each →" tag in the renderer
        },
      });
      container.children.push(legContainer);

      let legEntry = legContainer;
      let legExit  = legContainer;
      if (Array.isArray(b.children) && b.children.length > 0) {
        let prevExit = null;
        for (const child of b.children) {
          const { entry, exit, terminates } = this.emitIRNode(child, legContainer);
          if (prevExit) {
            this._addEdge(legContainer, prevExit.id, entry.id, { variant: 'trunk' });
          } else {
            legEntry = entry;
          }
          prevExit = terminates ? null : exit;
        }
        legExit = prevExit || legEntry;
      } else {
        // Template with no task yet — give it an anchor so the container
        // renders visibly as an empty "drop a task here" lane.
        const anchor = this._newChild({
          id: this._qualify('dynleg_anchor', `${ir.id}__${legName}`),
          width: V2_DIMS.MEET_W,
          height: V2_DIMS.MEET_H,
          _v2: { kind: 'meet', label: '', ownerForkId: ir.id, branchName: legName },
        });
        legContainer.children.push(anchor);
        legEntry = anchor;
        legExit  = anchor;
      }

      // G479 — Head → leg ENTRY (dynamic fork mirror of _emitForkLike).
      this._addEdge(container, head.id, legEntry.id, {
        variant: 'branch',
        branchLabel: null,
      });
      legExits.push({ legContainer, legEntry, legExit, terminates: _isTerminatedBranch(b) });
    });

    const joinIR = ir.join || {};
    const join = this._newChild({
      id: this._qualify('dynfork_join', ir.id),
      width: V2_DIMS.JOIN_W,
      height: V2_DIMS.JOIN_H,
      _v2: {
        kind: 'join',
        label: joinIR.label || 'Join',
        canvasType: 'parallel_join',
        waitMode: joinIR.waitMode || 'ALL',
        waitCount: joinIR.waitCount || null,
        joinOn: Array.isArray(joinIR.joinOn) ? joinIR.joinOn : [],
        dynamic: true,
        ownerForkId: ir.id,
        sourceNodeId: joinIR.id || null,
      },
    });
    container.children.push(join);

    // G479 — Leg EXIT → join (not legContainer → join). Same rationale as
    // the case-exit → meet fix in _emitSwitch.
    for (const exit of legExits) {
      if (exit.terminates) continue;
      this._addEdge(container, exit.legExit.id, join.id, {
        variant: 'convergence',
      });
    }

    parent.children.push(container);
    if (parent === this.root) this._rootOrder.push(container);
    return { entry: container, exit: container, tailPort: join };
  }

  _emitLoop(ir, parent) {
    // DoWhile container:
    //   loopContainer
    //     ├── loopHead            (condition expression, iteration hint)
    //     ├── loopBody            (sub-container holding the body tasks)
    //     │     └── …body IR children chained by trunk edges
    //     └── [self-loop back-edge] bodyTail → head, variant='loopBack'
    //
    // The back-edge is how Canvas V2 makes iteration visually obvious — it
    // teaches intuition in a way Conductor OSS's flat-line rendering doesn't.
    const container = this._newChild({
      id: this._qualify('loop_c', ir.id),
      layoutOptions: loopContainerLayoutOptions(),
      children: [],
      edges: [],
      _v2: {
        kind: 'loopContainer',
        label: ir.label,
        canvasType: 'loop',
        condition: ir.condition,
        iterationHint: ir.iterationHint || null,
        sourceNodeId: ir.id,
      },
    });

    const head = this._newChild({
      id: this._qualify('loop_head', ir.id),
      width: V2_DIMS.LOOP_HEAD_W,
      height: V2_DIMS.LOOP_HEAD_H,
      _v2: {
        kind: 'loopHead',
        label: ir.label,
        canvasType: 'loop',
        condition: ir.condition,
        iterationHint: ir.iterationHint || null,
        sourceNodeId: ir.id,
      },
    });
    container.children.push(head);

    const body = this._newChild({
      id: this._qualify('loop_body', ir.id),
      layoutOptions: loopBodyLayoutOptions(),
      children: [],
      edges: [],
      _v2: {
        kind: 'loopBody',
        label: 'body',
        ownerLoopId: ir.id,
      },
    });
    container.children.push(body);

    // Walk body children inside the body sub-container.
    const bodyChildren = Array.isArray(ir.body) ? ir.body : [];
    let bodyEntry = body;
    let bodyExit = body;
    if (bodyChildren.length > 0) {
      let prevExit = null;
      for (const child of bodyChildren) {
        const { entry, exit, terminates } = this.emitIRNode(child, body);
        if (prevExit) {
          const sourceId = _pickTrunkTail(prevExit) || prevExit.id;
          this._addEdge(body, sourceId, entry.id, { variant: 'trunk' });
        } else {
          bodyEntry = entry;
        }
        prevExit = terminates ? null : exit;
      }
      bodyExit = prevExit || bodyEntry;
    } else {
      // Empty body — synthesize a pass-through so the body sub-container has
      // an anchor ELK can size around.
      const passthrough = this._newChild({
        id: this._qualify('loop_empty', ir.id),
        width: V2_DIMS.MEET_W,
        height: V2_DIMS.MEET_H,
        _v2: { kind: 'meet', label: '', ownerLoopId: ir.id },
      });
      body.children.push(passthrough);
      bodyEntry = passthrough;
      bodyExit = passthrough;
    }

    // Head → body (trunk edge inside the loop container).
    this._addEdge(container, head.id, body.id, { variant: 'trunk' });

    // Body tail → head (the self-loop back-edge that teaches iteration).
    // We point at a concrete inner node (bodyExit) rather than the body
    // container itself so ELK can route it with proper bend points.
    const backSource = _pickTrunkTail(bodyExit) || bodyExit.id;
    this._addEdge(container, backSource, head.id, { variant: 'loopBack' });

    parent.children.push(container);
    if (parent === this.root) this._rootOrder.push(container);
    return {
      entry: container,
      exit: container,
      // Trunk stitcher: next-in-sequence starts from the body's tail so the
      // exit edge emerges from the bottom of the loop chrome naturally.
      tailPort: bodyExit,
    };
  }

  // ── Trunk stitching ──────────────────────────────────────────────

  /**
   * Connect every top-level child in insertion order with a trunk edge.
   * For switch containers, the "exit" is its internal meet node — ELK will
   * route the edge from the meet through the container boundary to the next
   * node because of `INCLUDE_CHILDREN` hierarchy handling.
   */
  stitchTrunk() {
    for (let i = 1; i < this._rootOrder.length; i++) {
      const prev = this._rootOrder[i - 1];
      const curr = this._rootOrder[i];
      const sourceId = _pickTrunkTail(prev) || prev.id;
      this._addEdge(this.root, sourceId, curr.id, { variant: 'trunk' });
    }
  }

  // ── internals ────────────────────────────────────────────────────

  _newChild(props) {
    // Every child gets a guaranteed-unique id; callers pass in a preferred
    // id and we fall back to a counter suffix if collisions show up.
    this._childCounter++;
    return { ...props };
  }

  _addEdge(owner, sourceId, targetId, meta = {}) {
    const id = `e_${this._edgeCounter++}__${sourceId}__${targetId}`;
    const edge = {
      id,
      sources: [sourceId],
      targets: [targetId],
      _v2: { variant: meta.variant || 'trunk', ...meta },
    };
    if (meta.branchLabel != null && meta.branchLabel !== 'default' && meta.branchLabel !== 'out') {
      const text = String(meta.branchLabel);
      edge.labels = [{
        text,
        width: Math.min(Math.max(24, text.length * 7 + 20), 160),
        height: 20,
        layoutOptions: {
          'elk.edgeLabels.placement': 'CENTER',
          'elk.edgeLabels.inline': 'true',
        },
      }];
    }
    owner.edges.push(edge);
    return edge;
  }

  _qualify(prefix, rawId) {
    return `${prefix}__${String(rawId).replace(/[^A-Za-z0-9_]/g, '_')}`;
  }
}

// ───────────────────────────────────────────────────────────────────
//  Layout-option presets
// ───────────────────────────────────────────────────────────────────
//
// These are tuned for Switch in this cut. Fork / DoWhile will likely want
// their own presets (wider branch splay for forks, tighter body padding for
// loops) — they'll land next to this file when #161/#162 do.

function rootLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    // Respect insertion order when laying out top-level children — this keeps
    // Start → body → End on a vertical spine instead of letting BRANDES_KOEPF
    // drift End off-axis when a wide switch container dominates the middle.
    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    'elk.layered.thoroughness': '20',
    'elk.edgeRouting': 'ORTHOGONAL',
    // Tightened horizontal breathing room at the root — containers carry their
    // own internal padding, so the root spine doesn't need extra.
    'elk.spacing.nodeNode': '22',
    'elk.layered.spacing.nodeNodeBetweenLayers': '35',
    'elk.layered.spacing.edgeNodeBetweenLayers': '15',
    'elk.layered.spacing.edgeEdgeBetweenLayers': '10',
    'elk.spacing.edgeLabel': '10',
    'elk.padding': '[top=20,left=20,bottom=20,right=20]',
  };
}

function switchContainerLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.thoroughness': '20',
    'elk.edgeRouting': 'ORTHOGONAL',
    // G481 — Tightened from 32 → 16. Wide switches (e.g. route_event with 4
    // cases) were burning 32px × (N-1) on empty air between cases, blowing
    // canvas width to 4000+ px on IdentityOnboadring v4. Case-container
    // chrome already paints a visible border around each arm; the gap
    // between cases doesn't need to do that work too.
    //
    // NOTE: considerModelOrder.strategy is deliberately NOT set here. It's
    // applied only at the root (where it fixes the Start→End column drift).
    // Setting it on nested containers trips an ELK internal crash when
    // combined with INCLUDE_CHILDREN + synthetic meet/passthrough nodes
    // (reproduced on switch-in-switch and switch-in-fork fixtures).
    'elk.spacing.nodeNode': '10',
    'elk.layered.spacing.nodeNodeBetweenLayers': '30',
    'elk.padding': `[top=${V2_DIMS.CONTAINER_PAD},left=${V2_DIMS.CONTAINER_PAD},bottom=${V2_DIMS.CONTAINER_PAD},right=${V2_DIMS.CONTAINER_PAD}]`,
  };
}

function caseContainerLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.edgeRouting': 'ORTHOGONAL',
    // G481 — Tighter intra-case spacing as well; case columns stack vertical
    // chains so horizontal nodeNode mostly affects parallel sub-structure
    // (e.g. an implicit fork inside a case). 32 → 20.
    'elk.spacing.nodeNode': '12',
    'elk.layered.spacing.nodeNodeBetweenLayers': '25',
    'elk.padding': `[top=${V2_DIMS.CASE_PAD},left=${V2_DIMS.CASE_PAD},bottom=${V2_DIMS.CASE_PAD},right=${V2_DIMS.CASE_PAD}]`,
  };
}

// Fork containers need wider splay than switch cases — parallel legs fan
// out symmetrically around the head/join spine. BALANCED alignment keeps
// the middle leg on the trunk for odd leg counts, matching Conductor OSS's fixtures.
function forkContainerLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.thoroughness': '20',
    'elk.edgeRouting': 'ORTHOGONAL',
    // Tighter horizontal pitch than switch (fork legs tend to be narrower) —
    // leg containers add their own internal LEG_PAD on top of this.
    'elk.spacing.nodeNode': '20',
    'elk.layered.spacing.nodeNodeBetweenLayers': '35',
    'elk.padding': `[top=${V2_DIMS.CONTAINER_PAD},left=${V2_DIMS.CONTAINER_PAD},bottom=${V2_DIMS.CONTAINER_PAD},right=${V2_DIMS.CONTAINER_PAD}]`,
  };
}

function legContainerLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.spacing.nodeNode': '17',
    'elk.layered.spacing.nodeNodeBetweenLayers': '22',
    'elk.padding': `[top=${V2_DIMS.LEG_PAD},left=${V2_DIMS.LEG_PAD},bottom=${V2_DIMS.LEG_PAD},right=${V2_DIMS.LEG_PAD}]`,
  };
}

// Loop containers lay out vertically: head, body, and the back-edge routes
// around them. ELK's orthogonal router handles the curved back-edge; we just
// make sure there's enough nodeNodeBetweenLayers room for the routing.
function loopContainerLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.spacing.nodeNode': '20',
    'elk.layered.spacing.nodeNodeBetweenLayers': '40',  // extra for back-edge
    'elk.padding': `[top=${V2_DIMS.CONTAINER_PAD},left=${V2_DIMS.CONTAINER_PAD},bottom=${V2_DIMS.CONTAINER_PAD},right=${V2_DIMS.CONTAINER_PAD}]`,
  };
}

function loopBodyLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.spacing.nodeNode': '17',
    'elk.layered.spacing.nodeNodeBetweenLayers': '25',
    'elk.padding': `[top=${V2_DIMS.CASE_PAD},left=${V2_DIMS.CASE_PAD},bottom=${V2_DIMS.CASE_PAD},right=${V2_DIMS.CASE_PAD}]`,
  };
}

// SubWorkflow container — a wrapped nested DAG. Layout-wise it's essentially
// a loopBody with a headline at the top, so we mirror loopContainerLayoutOptions
// but without the extra back-edge breathing room.
function subWorkflowContainerLayoutOptions() {
  return {
    'elk.algorithm': 'layered',
    'elk.direction': 'DOWN',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.spacing.nodeNode': '20',
    'elk.layered.spacing.nodeNodeBetweenLayers': '30',
    'elk.padding': `[top=${V2_DIMS.CONTAINER_PAD},left=${V2_DIMS.CONTAINER_PAD},bottom=${V2_DIMS.CONTAINER_PAD},right=${V2_DIMS.CONTAINER_PAD}]`,
  };
}

// ───────────────────────────────────────────────────────────────────
//  Small helpers
// ───────────────────────────────────────────────────────────────────

function _isTerminatedCase(caseObj) {
  // A case terminates when its last child is a Terminate IR node. Empty
  // cases converge on the meet (by our synthetic pass-through).
  if (!caseObj || !Array.isArray(caseObj.children) || caseObj.children.length === 0) return false;
  const last = caseObj.children[caseObj.children.length - 1];
  return last && last.kind === IR_KIND.TERMINATE;
}

function _isTerminatedBranch(branchObj) {
  // Mirror of _isTerminatedCase for fork legs — a leg whose last task is a
  // TERMINATE should NOT fan into the JOIN (it blows up the workflow early).
  // Empty legs still need to converge on JOIN via their synthetic anchor.
  if (!branchObj || !Array.isArray(branchObj.children) || branchObj.children.length === 0) return false;
  const last = branchObj.children[branchObj.children.length - 1];
  return last && last.kind === IR_KIND.TERMINATE;
}

/**
 * For a top-level child that represents a container, the correct source for
 * the next trunk edge is the container's internal tail (a `meet` synthetic
 * node for switch, or a `join` node for fork family), because ELK's
 * INCLUDE_CHILDREN routing threads the edge through the container boundary.
 * For scalars, just return the child's own id.
 */
function _pickTrunkTail(rootChild) {
  if (!rootChild || !rootChild._v2) return rootChild?.id || null;
  if (rootChild._v2.kind === 'switchContainer') {
    const meet = (rootChild.children || []).find((c) => c._v2 && c._v2.kind === 'meet' && !c._v2.branchName);
    if (meet) return meet.id;
  }
  if (rootChild._v2.kind === 'forkContainer' || rootChild._v2.kind === 'dynForkContainer') {
    const join = (rootChild.children || []).find((c) => c._v2 && c._v2.kind === 'join');
    if (join) return join.id;
  }
  if (rootChild._v2.kind === 'loopContainer') {
    // Trunk continues FROM the loop body's tail task — that's the node the
    // exit-edge emerges from. Find the body sub-container then its last task.
    const body = (rootChild.children || []).find((c) => c._v2 && c._v2.kind === 'loopBody');
    if (body && Array.isArray(body.children) && body.children.length > 0) {
      // Walk to the last non-passthrough child; if only a passthrough exists,
      // that's fine — ELK will route from it through the container boundary.
      const last = body.children[body.children.length - 1];
      return _pickTrunkTail(last) || last.id;
    }
  }
  if (rootChild._v2.kind === 'subWorkflowContainer') {
    // Trunk continues from the last embedded child; fall back to the head
    // if we couldn't find anything better.
    const head = (rootChild.children || []).find((c) => c._v2 && c._v2.kind === 'subWorkflowHead');
    const body = (rootChild.children || []).filter(
      (c) => c._v2 && c._v2.kind !== 'subWorkflowHead',
    );
    if (body.length > 0) {
      const last = body[body.length - 1];
      return _pickTrunkTail(last) || last.id;
    }
    if (head) return head.id;
  }
  return rootChild.id;
}
